import { createSlice } from '@reduxjs/toolkit';
import { MostPracticedScenarios, getPracticeScenarios, pinScenario, unpinScenario } from '@/store/actions/scenarios';
import { DonorScenario, ScenariosState } from '@/types/scenarios';

const initialState: ScenariosState = {
  scenarios: {
    data: [],
    limit: 0,
    page: 0,
    total: 0
  },
  mostPracticedScenarios: [],
  loading: false,
  error: null
};

const sortScenariosByPinned = (scenarios: DonorScenario[]): DonorScenario[] => {
  return [...scenarios].sort((a, b) => {
    // Sort by pinned status first (true comes before false)
    if (a.pinned !== b.pinned) {
      return a.pinned ? -1 : 1;
    }
    // If pinned status is the same, sort by updatedAt date (newest first)
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
};

const handleToggleScenarioPin = (scenarios: DonorScenario[], scenarioId: string) => {
  return scenarios.map(scenario => {
    if (scenario.id === scenarioId) {
      return { ...scenario, pinned: !scenario.pinned };
    }
    return scenario;
  });
};

export const scenariosSlice = createSlice({
  name: 'scenarios',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getPracticeScenarios.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPracticeScenarios.fulfilled, (state, action) => {
        state.loading = false;
        state.scenarios = action.payload;
      })
      .addCase(getPracticeScenarios.rejected, (state, action) => {
        state.loading = false;
        state.scenarios = initialState.scenarios;
        state.error = action.payload || action.error.message || 'Failed to fetch practice scenarios';
      })

      .addCase(MostPracticedScenarios.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(MostPracticedScenarios.fulfilled, (state, action) => {
        state.loading = false;
        // @ts-ignore TODO: fix with correct type
        state.mostPracticedScenarios = action.payload.data.scenarios;
      })
      .addCase(MostPracticedScenarios.rejected, (state, action) => {
        state.loading = false;
        state.mostPracticedScenarios = [];
        state.error = action.payload || action.error.message || 'Failed to fetch most practiced scenarios';
      })

      .addCase(pinScenario.pending, (state, action) => {
        const scenarioId = action.meta.arg;

        state.scenarios.data = handleToggleScenarioPin(state.scenarios.data, scenarioId);
        state.scenarios.data = sortScenariosByPinned(state.scenarios.data);
      })
      .addCase(pinScenario.rejected, (state, action) => {
        state.error = (action.payload as string) || 'Failed to pin scenario';

        const scenarioId = action.meta.arg;

        state.scenarios.data = handleToggleScenarioPin(state.scenarios.data, scenarioId);
        state.scenarios.data = sortScenariosByPinned(state.scenarios.data);
      })

      .addCase(unpinScenario.pending, (state, action) => {
        const scenarioId = action.meta.arg;

        state.scenarios.data = handleToggleScenarioPin(state.scenarios.data, scenarioId);
        state.scenarios.data = sortScenariosByPinned(state.scenarios.data);
      })
      .addCase(unpinScenario.rejected, (state, action) => {
        state.error = (action.payload as string) || 'Failed to pin scenario';

        const scenarioId = action.meta.arg;

        state.scenarios.data = handleToggleScenarioPin(state.scenarios.data, scenarioId);
        state.scenarios.data = sortScenariosByPinned(state.scenarios.data);
      });
  }
});

export default scenariosSlice.reducer;
